import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Inject, OnDestroy, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyDialog as MatDialog, MatLegacyDialogModule as MatDialogModule, MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { MatLegacyListModule as MatListModule } from '@angular/material/legacy-list';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { FileEntity } from '../../../dave-data-module/entities/file.entity';
import { FileDataService } from '../../../dave-data-module/services/file-data.service';
import { State } from '../../../dave-data-module/State';
import { FilesActionTypes } from '../../../dave-data-module/State/actions/files.actions';
import { AppButtonModule } from '../../../dave-utils-module/app-button-module/app-button.module';
import { AppConfirmationDialogComponent, AppConfirmationDialogData } from '../../../dave-utils-module/app-dialog-module/app-confirmation-dialog/app-confirmation-dialog.component';
import { DetailListDialogReturn, DetailListTemplateDialogComponent, DetailListTemplateDialogData } from '../../../detail-list-template-dialog/components/detail-list-template-dialog.component';
import { ExpandableSearchbarComponent } from '../../../expandable-searchbar/expandable-searchbar.component';

export interface FileOrderDialogComponentDialogData {
    fileIds: number[];
    withDelete?: boolean;
    withVersionUploadButton?: boolean;
    deleteDialogData?: AppConfirmationDialogData;
    headline?: string;
    searchPlaceholder?: string;
}
export type FileOrderDialogComponentDialogReturnData = number[];
@Component({
    selector: 'app-file-order-dialog',
    standalone: true,
    imports: [CommonModule, MatListModule, FontAwesomeModule, DragDropModule, MatButtonModule, MatDialogModule, AppButtonModule, MatMenuModule, MatTooltipModule, ExpandableSearchbarComponent],
    templateUrl: './file-order-dialog.component.html',
    styleUrls: ['./file-order-dialog.component.scss'],
})
export class FileOrderDialogComponent implements OnDestroy {
    protected files$ = new BehaviorSubject<FileEntity[]>([]);
    private fileSub: Subscription;
    protected editDialogRef: MatDialogRef<any>;
    protected searchQuery$ = new BehaviorSubject<string>('');
    protected filteredFiles$ = this.files$.pipe(switchMap((files) => this.searchQuery$.pipe(map((query) => (query ? files.filter((f) => f.Name.toLowerCase().includes(query.toLowerCase())) : files)))));

    constructor(
        private dialog: MatDialog,
        @Inject(MAT_DIALOG_DATA) private dialogData: FileOrderDialogComponentDialogData,
        private fileDataService: FileDataService,
        private store: Store<State>,
        private dialogRef: MatDialogRef<FileOrderDialogComponent>,
    ) {
        if (dialogData.headline) {
            this.headline = dialogData.headline;
        }
        if (dialogData.fileIds.length) {
            this.fileSub = this.fileDataService.GetFilesById(dialogData.fileIds).subscribe((files) => {
                this.files$.next(dialogData.fileIds.map((id) => files.find((f) => f.Id === id)));
            });
        }
    }
    @Output() public deleteFile = new EventEmitter<number>();
    @Output() public versionUpload = new EventEmitter<number>();
    @Output() public versionUploadDms = new EventEmitter<number>();
    @ViewChild('fileUploadTemplate') fileUploadTemplate: TemplateRef<any>;
    protected headline = 'Datei Reihenfolge';
    protected searchPlaceholder = 'Dateien durchsuchen...';

    drop(event: CdkDragDrop<string[]>) {
        moveItemInArray(this.files$.value, event.previousIndex, event.currentIndex);
    }

    ngOnDestroy(): void {
        this.fileSub?.unsubscribe();
        this.editDialogRef?.close(null);
    }

    onEditFileClick(file: FileEntity) {
        const form = new FormGroup({
            name: new FormControl<string>(file.Name),
            description: new FormControl<string>(file.Description),
        });
        this.editDialogRef = this.dialog.open<DetailListTemplateDialogComponent, DetailListTemplateDialogData, DetailListDialogReturn>(DetailListTemplateDialogComponent, {
            data: {
                DeleteButton: this.dialogData?.withDelete,
                DeleteButtonTooltip: 'Plan löschen',
                Editing: true,
                Data: {
                    Headline: 'Datei bearbeiten',
                    Properties: [
                        ...(this.dialogData?.withVersionUploadButton
                            ? [
                                  {
                                      key: 'upload',
                                      formControl: new FormControl<number>(file.Id),
                                      options: {
                                          specialInput: {
                                              customTemplate: this.fileUploadTemplate,
                                          },
                                      },
                                  },
                              ]
                            : []),
                        {
                            key: 'Name',
                            formControl: form.controls.name,
                        },
                        {
                            key: 'Beschreibung',
                            formControl: form.controls.description,
                        },
                    ],
                },
            },
        });
        this.editDialogRef.afterClosed().subscribe((result) => {
            if (result?.Action === 'save') {
                this.store.dispatch(
                    FilesActionTypes.ModifyFileRequest({
                        Payload: {
                            DocumentId: file.Id.toString(),
                            Name: form.value.name,
                            Description: form.value.description,
                        },
                    }),
                );
            } else if (result?.Action === 'delete') {
                this.dialog
                    .open<AppConfirmationDialogComponent, AppConfirmationDialogData>(AppConfirmationDialogComponent, {
                        data: {
                            paragraph: 'Wollen Sie die Datei wirklich löschen ?',
                            styleDelete: true,
                            ...(this.dialogData?.deleteDialogData || {}),
                        },
                    })
                    .afterClosed()
                    .subscribe(([res]) => {
                        if (res) {
                            this.files$.next(this.files$.value.filter((f) => f.Id !== file.Id));
                            this.deleteFile.emit(file.Id);
                        }
                    });
            }
        });
    }

    onSaveClick() {
        this.dialogRef.close(this.files$.value.map((f) => f.Id));
    }
}
